import { UpdateRequest } from 'api/types'
import { diff } from 'deep-object-diff'
import { configActions, estimateActions } from 'ducks/actions'
import { EstimateType } from 'ducks/estimate/types'
import { EstimateContact, EstimateProperties, PunchlistFile } from 'ducks/types'
import {
  APPROVAL_TYPE,
  CONTACT_ROLE,
  ESTIMATE_STATUS,
  FILE_TYPE,
  companyRoles,
  getContactRoleOpt,
  getDifferenceBetweenObjects,
  history,
  isEmpty,
  objectToUpdateRequest,
  validateEmail,
  validatePhone,
} from 'helpers/index'
import { FC, useEffect, useState } from 'react'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useDispatch, useSelector } from 'react-redux'
import {
  getApprovalStep,
  getApprovalValues,
  getContactInEstimate,
  getContactIndexInEstimate,
  getEstimate,
  getUser,
  getUserIsHomeowner,
  getUserPrimaryEmail,
} from '../../../../../../../../ducks/selectors'
import { useStepperContext } from '../../../../../../../../hooks/useStepperContext'
import { Autocomplete, Grid, Typography } from '../../../../../../../UI'
import AccessDetails from '../../../components/AccessDetails'
import ApproximateHomeEquity from '../../../components/ApproximateHomeEquity'
import BottomButtons from '../../../components/BottomButtons'
import ComparableMarketAnalysis from '../../../components/ComparableMarketAnalysis'
import HomeownershipConfirmation from '../../../components/HomeownershipConfirmation'
import ListingAgentInformation from '../../../components/ListingAgentInformation'
import ListingAgreement from '../../../components/ListingAgreement'
import PayAtClose from '../../../components/PayAtClose'
import PropertyStatus from '../../../components/PropertyStatus'
import SchedulerContact from '../../../components/SchedulerContact'
import Scheduling from '../../../components/Scheduling'
import useStyles from './../../../styles'
import Financing from '../../../components/Financing'

const Step1Homeowner: FC = () => {
  const classes = useStyles()
  const [listingAgreementDocument, setListingAgreementDocument] = useState<
    PunchlistFile[]
  >([])
  const [comparableMarketAnalysis, setComparableMarketAnalysis] = useState<
    PunchlistFile[]
  >([])
  const [stepPayAtClose, setStepPayAtClose] = useState(false)
  const { setShowWarnings, setCanGoForward, showWarnings, saveNewValue } =
    useStepperContext()
  const push = history.usePushForward()
  const goBack = history.useGoBack()
  const dispatch = useDispatch()
  const estimate = useSelector(getEstimate())
  const user = useSelector(getUser)
  const userEmail = useSelector(getUserPrimaryEmail)
  const approvalStep = useSelector(getApprovalStep)
  const alreadyUpdatedListingAgreement = estimate?.properties?.files?.find(
    (file) => file.fileType === FILE_TYPE.LISTING_AGREEMENT
  )
  const alreadyUpdatedComparableMarketAnalysis =
    estimate?.properties?.files?.find(
      (file) => file.fileType === FILE_TYPE.COMPARABLE_MARKET_ANALYSIS
    )
  const fileIndex = estimate?.properties?.files?.findIndex(
    (file) => file.fileType === FILE_TYPE.LISTING_AGREEMENT
  )
  const comparableMarketAnalysisfileIndex =
    estimate?.properties?.files?.findIndex(
      (file) => file.fileType === FILE_TYPE.COMPARABLE_MARKET_ANALYSIS
    )
  const contactInEstimateIndex = useSelector(
    getContactIndexInEstimate(userEmail)
  )

  const {
    preferredCompletedOn,
    preferredStartDate,
    lockboxConsent,
    schedulerSelect,
    propertyToBeSold,
    scheduler,
    propertyUnderContract,
    closingDate,
    propertyListed,
    completedBeforeClosing,
    paymentOption,
    lockbox,
    accessDetails,
    role,
    agentIsHomeowner,
    listingAgent,
    agentAsHomeownerRepresentative,
    homeownershipConfirmation,
    approximateHomeEquity,
    financingOption
  } = useSelector(getApprovalValues())

  const contactInEstimate = useSelector(getContactInEstimate(userEmail))

  const showPayAtClose = contactInEstimate?.role !== CONTACT_ROLE.HOME_BUYER

  const agentContact = estimate?.properties?.contacts?.find(
    (c) =>
      companyRoles.includes(c.role as CONTACT_ROLE) && c.email !== userEmail
  )

  const schedulerFromEstimateIndex = estimate?.properties?.contacts?.findIndex(
    (c) => c?.isScheduler
  )
  const schedulerFromEstimate = estimate?.properties?.contacts?.find(
    (c) => c?.isScheduler
  )

  const showPayAtCompletionFlow =
    paymentOption === 'pay_at_completion' || agentIsHomeowner

  const isHomeowner = agentIsHomeowner || contactInEstimate?.role === CONTACT_ROLE.HOMEOWNER

  const schedularContactValidation =
    schedulerSelect === 'other'
      ? !!scheduler?.role &&
      !!scheduler.firstName &&
      !!scheduler.lastName &&
      !!scheduler.email &&
      validateEmail(scheduler.email) &&
      !!scheduler.phone &&
      validatePhone(scheduler?.phone)
      : true

  const listingAgentValidation =
    agentAsHomeownerRepresentative !== 'no_selected'
      ? agentAsHomeownerRepresentative === 'listing_agent' || !agentIsHomeowner
        ? !!listingAgent?.companyName &&
        !!listingAgent?.firstName &&
        !!listingAgent?.lastName &&
        !!listingAgent?.email &&
        validateEmail(listingAgent?.email) &&
        !!listingAgent?.phone &&
        validatePhone(listingAgent?.phone)
        : agentAsHomeownerRepresentative === 'myself'
          ? propertyUnderContract
            ? !!listingAgreementDocument.length
            : !!listingAgreementDocument.length &&
            !!comparableMarketAnalysis.length &&
            !!approximateHomeEquity
          : true
      : false

  const houseInformationValidation = () => {
    if (propertyToBeSold !== undefined) {
      if (propertyToBeSold) {
        if (propertyUnderContract !== undefined) {
          if (propertyUnderContract) {
            return (
              !isEmpty(closingDate?.toString()) &&
              completedBeforeClosing !== undefined &&
              completedBeforeClosing !== null
            )
          } else {
            return propertyListed !== undefined && propertyListed !== null
          }
        }
      } else {
        return true
      }
    }
  }

  const disableNext = !!estimate?.properties.contacts?.find(
    (contact) => contact.approvalFlowInProgress && contact.email !== userEmail
  )

  useEffect(() => {
    if (paymentOption === "pay_at_completion" && stepPayAtClose) {
      setStepPayAtClose(false)
    }
  }, [paymentOption, stepPayAtClose])

  const isBosscatFinancing = financingOption === 'Bosscat';
  const isHouseAmpFinancing = financingOption === 'HouseAmp';

  const flowValidation =
    typeof preferredStartDate !== 'undefined' &&
    typeof preferredCompletedOn !== 'undefined' &&
    (lockbox || !!lockboxConsent) &&
    schedularContactValidation &&
    houseInformationValidation() &&
    homeownershipConfirmation



  const validate = () => {
    if (disableNext) return false

    if (paymentOption === 'pay_at_close') {
      if (agentIsHomeowner) {
        return (agentContact || listingAgentValidation) && flowValidation
      }
      if (isBelow10K || estimate?.properties.financingRejectedOn || isBosscatFinancing) {
        return agentContact || listingAgentValidation
      } else {
        return houseInformationValidation() &&
          homeownershipConfirmation
      }
    }
    return flowValidation
  }

  useEffect(() => {
    setShowWarnings(false)
  }, [])

  useEffect(() => {
    setCanGoForward(validate() as boolean)
  }, [validate])

  useEffect(() => {
    if (alreadyUpdatedListingAgreement) {
      setListingAgreementDocument([alreadyUpdatedListingAgreement])
    }
    if (alreadyUpdatedComparableMarketAnalysis) {
      setComparableMarketAnalysis([alreadyUpdatedComparableMarketAnalysis])
    }
  }, [alreadyUpdatedListingAgreement])

  const submitStep = () => {
    if (paymentOption === 'pay_at_close' && !stepPayAtClose && !isBelow10K && !estimate?.properties.financingRejectedOn && !isBosscatFinancing) {
      return setStepPayAtClose(true)
    }
    if (paymentOption === 'pay_at_close' && !isBelow10K && stepPayAtClose) {
      if (isHouseAmpFinancing) {
        window.open('https://secure.houseamp.com/#/homeowner-signup?from=cfd1f2ee-27e7-440e-93e7-97de24d4675c', '_blank')
      } else {

      }
    }
    dispatch(
      configActions.setConfigValue({ type: 'overlayLoading', value: true })
    )
    updateEstimate()
  }

  const updateEstimate = () => {
    if (paymentOption === 'pay_at_close') {
      const agentIndex = estimate?.properties?.contacts?.findIndex(
        (c) => c.id === agentContact?.id
      )

      const payload: UpdateRequest = [
        { op: 'replace', path: '/properties/payAtClose', value: true },
        { op: 'replace', path: '/properties/propertyToBeSold', value: true },
        {
          op: 'replace',
          path: '/properties/homeownershipConfirmation',
          value: homeownershipConfirmation,
        },
      ]

      if (!isBelow10K && !estimate?.properties.financingApprovedOn && !estimate?.properties.financingRejectedOn && !isBosscatFinancing) {
        payload.push({
          op: 'replace',
          path: '/status',
          value: ESTIMATE_STATUS.AWAITING_FINANCING
        })
      }

      if (agentAsHomeownerRepresentative !== 'myself') {
        //remove the approvalFlowInProgress in case they went to next step and came back to select pay at close, only if they didn't select themselves as representative (they will then need to continue the flow)

        payload.push({
          op: 'replace',
          path: `/properties/contacts/${contactInEstimateIndex}/approvalFlowInProgress`,
          value: false,
        })
        payload.push({
          op: 'replace',
          path: `/properties/contacts/${contactInEstimateIndex}/approvalType`,
          value: APPROVAL_TYPE.REQUIRED,
        })
      } else {
        payload.push(
          {
            op: 'replace',
            path: `/properties/approvalStep`,
            value: approvalStep + 1,
          },
          {
            op: 'replace',
            path: `/properties/contacts/${contactInEstimateIndex}/approvalFlowInProgress`,
            value: true,
          }
        )
      }

      if (agentIndex === -1 || agentIsHomeowner) {
        //If there's no agent in the estimate we add it
        if (
          agentAsHomeownerRepresentative === 'listing_agent' ||
          !agentIsHomeowner
        ) {
          //If the agent selected that has a listing agent or the user is a homeowner we add the listing agent info.
          payload.push({
            op: 'add',
            path: `/properties/contacts/-`,
            value: {
              ...listingAgent,
              approvalType: APPROVAL_TYPE.REQUIRED,
              role: CONTACT_ROLE.LISTING_AGENT,
            },
          })
          payload.push({
            op: 'replace',
            path: `/properties/contacts/${contactInEstimateIndex}/role`,
            value: CONTACT_ROLE.HOMEOWNER,
          })
        } else {
          //If it's an agent and selected 'myself' as the representative we change the contact role to AGENT_HOMEOWNER
          payload.push({
            op: 'replace',
            path: `/properties/contacts/${contactInEstimateIndex}/role`,
            value: CONTACT_ROLE.AGENT_HOMEOWNER,
          })
          payload.push({
            op: 'replace',
            path: `/properties/contacts/${contactInEstimateIndex}/isPayer`,
            value: true,
          })
        }
      } else {
        payload.push({
          op: 'replace',
          path: `/properties/contacts/${agentIndex}/approvalType`,
          value: APPROVAL_TYPE.REQUIRED,
        })
      }

      if (agentAsHomeownerRepresentative === 'myself') {
        payload.push({
          op: alreadyUpdatedListingAgreement ? 'replace' : 'add',
          path: `/properties/files/${alreadyUpdatedListingAgreement ? fileIndex : '-'
            }`,
          value: {
            fileType: FILE_TYPE.LISTING_AGREEMENT,
            fileUrl: listingAgreementDocument[0]?.fileUrl,
            fileName: listingAgreementDocument[0]?.name,
            name: listingAgreementDocument[0]?.name,
            description: 'Listing Agreement',
          },
        })
        if (!estimate?.properties?.propertyUnderContract) {
          payload.push(
            {
              op: alreadyUpdatedComparableMarketAnalysis ? 'replace' : 'add',
              path: `/properties/files/${alreadyUpdatedComparableMarketAnalysis
                ? comparableMarketAnalysisfileIndex
                : '-'
                }`,
              value: {
                fileType: FILE_TYPE.COMPARABLE_MARKET_ANALYSIS,
                fileUrl: comparableMarketAnalysis[0]?.fileUrl,
                fileName: comparableMarketAnalysis[0]?.name,
                name: comparableMarketAnalysis[0]?.name,
                description: 'Comparable Market Analysis',
              },
            },
            {
              op: 'replace',
              path: '/properties/approximateHomeEquity',
              value: approximateHomeEquity,
            }
          )
        }
      }

      let valuesToSend: Partial<EstimateProperties> = {}
      // const contactsPayload: UpdateRequest = []
      if (agentIsHomeowner) {
        valuesToSend = {
          preferredStartDate,
          preferredCompletedOn,
        }
        if (schedulerSelect === 'me') {
          // if the logged user is the scheduler we remove the scheduler from the estimate and add isScheduler to the user
          payload.push({
            op: 'replace',
            path: `/properties/contacts/${contactInEstimateIndex}/isScheduler`,
            value: true,
          })

          if (
            schedulerFromEstimateIndex !== -1 &&
            schedulerFromEstimate?.email !== userEmail
          ) {
            payload.push({
              op: 'replace',
              path: `/properties/contacts/${schedulerFromEstimateIndex}/isScheduler`,
              value: false,
            })
          }
        } else {
          if (schedulerFromEstimate?.id !== scheduler?.id) {
            //we remove the isScheduler flag from the previous scheduler
            payload.push({
              op: 'replace',
              path: `/properties/contacts/${schedulerFromEstimateIndex}/isScheduler`,
              value: false,
            })
          }
          // if other we need to remove the flag from the 'me' user if existed
          if (scheduler?.id) {
            const differences: any =
              schedulerFromEstimate && diff(schedulerFromEstimate, scheduler)

            const schedulerDifferences: UpdateRequest =
              getDifferenceBetweenObjects(
                differences,
                undefined,
                `properties/contacts/${schedulerFromEstimateIndex}`
              )

            if (!differences?.email) {
              payload.push(...schedulerDifferences)
            } else {
              delete scheduler.id
              delete scheduler.contactId
              //if the user is trying to edit the email for an already existing scheduler we need to remove the scheduler flag from the previous one and add the new contact
              payload.push({
                op: 'replace',
                path: `/properties/contacts/${schedulerFromEstimateIndex}/isScheduler`,
                value: false,
              })
              payload.push({
                op: 'add',
                path: `/properties/contacts/-`,
                value: { ...scheduler, isScheduler: true },
              })
            }
          } else {
            payload.push({
              op: 'add',
              path: `/properties/contacts/-`,
              value: { ...scheduler, isScheduler: true },
            })
          }
        }
      }
      if (propertyToBeSold) {
        valuesToSend = {
          ...valuesToSend,
          propertyUnderContract,
          closingDate,
          completedBeforeClosing,
          propertyListed,
          lockboxConsent,
          lockbox: lockbox ?? '',
          accessDetails,
          smsConsent: true,
          payAtClose: true,
        }
      }

      dispatch(
        estimateActions.updateEstimateByPath(
          [...payload, ...objectToUpdateRequest(valuesToSend, '/properties/')],
          (succ) => {
            dispatch(
              configActions.setConfigValue({
                type: 'overlayLoading',
                value: false,
              })
            )
            setStepPayAtClose(false)

            if (succ) {
              if (!isBelow10K && !estimate?.properties.financingApprovedOn && paymentOption === 'pay_at_close' && !estimate?.properties.financingRejectedOn && !isBosscatFinancing) {
                return push(`successFinancing`)
              }
              if (agentAsHomeownerRepresentative !== 'myself') {
                push(`success`)
              }
            }
          }
        )
      )
    } else {
      let valuesToSend: Partial<EstimateProperties> = {
        preferredStartDate,
        preferredCompletedOn,
        closingDate,
        completedBeforeClosing,
        propertyToBeSold,
        lockbox: lockbox ?? '',
        accessDetails,
        smsConsent: true,
        lockboxConsent,
        approvalStep: approvalStep + 1,
        payAtClose: false,
        homeownershipConfirmation: homeownershipConfirmation,
      }

      if (propertyToBeSold) {
        valuesToSend = {
          ...valuesToSend,
          propertyUnderContract,
          closingDate,
          completedBeforeClosing,
          propertyListed,
        }
      } else {
        valuesToSend = {
          ...valuesToSend,
          propertyUnderContract: false,
          propertyListed: false,
          closingDate: null,
          payAtClose: false,
        }
      }

      const contactsPayload: UpdateRequest = []

      contactsPayload.push({
        op: 'replace',
        path: `/properties/contacts/${contactInEstimateIndex}/approvalFlowInProgress`,
        value: true,
      })

      if (agentIsHomeowner) {
        // if agent is homeowner, we need to update the role
        contactsPayload.push({
          op: 'replace',
          path: `/properties/contacts/${contactInEstimateIndex}/role`,
          value: role,
        })
      }

      if (schedulerSelect === 'me') {
        // if the logged user is the scheduler we remove the scheduler from the estimate and add isScheduler to the user
        contactsPayload.push({
          op: 'replace',
          path: `/properties/contacts/${contactInEstimateIndex}/isScheduler`,
          value: true,
        })

        if (
          schedulerFromEstimateIndex !== -1 &&
          schedulerFromEstimate?.email !== userEmail
        ) {
          contactsPayload.push({
            op: 'replace',
            path: `/properties/contacts/${schedulerFromEstimateIndex}/isScheduler`,
            value: false,
          })
        }
      } else {
        if (schedulerFromEstimate?.id !== scheduler?.id) {
          //we remove the isScheduler flag from the previous scheduler
          contactsPayload.push({
            op: 'replace',
            path: `/properties/contacts/${schedulerFromEstimateIndex}/isScheduler`,
            value: false,
          })
        }
        // if other we need to remove the flag from the 'me' user if existed
        if (scheduler?.id) {
          const differences: any =
            schedulerFromEstimate && diff(schedulerFromEstimate, scheduler)

          const schedulerDifferences: UpdateRequest =
            getDifferenceBetweenObjects(
              differences,
              undefined,
              `properties/contacts/${schedulerFromEstimateIndex}`
            )

          if (!differences?.email) {
            contactsPayload.push(...schedulerDifferences)
          } else {
            delete scheduler.id
            delete scheduler.contactId
            //if the user is trying to edit the email for an already existing scheduler we need to remove the scheduler flag from the previous one and add the new contact
            contactsPayload.push({
              op: 'replace',
              path: `/properties/contacts/${schedulerFromEstimateIndex}/isScheduler`,
              value: false,
            })
            contactsPayload.push({
              op: 'add',
              path: `/properties/contacts/-`,
              value: { ...scheduler, isScheduler: true },
            })
          }
        } else {
          contactsPayload.push({
            op: 'add',
            path: `/properties/contacts/-`,
            value: { ...scheduler, isScheduler: true },
          })
        }
      }

      dispatch(
        estimateActions.updateEstimateByPath(
          [
            ...contactsPayload,
            ...objectToUpdateRequest(valuesToSend, '/properties/'),
          ],
          (succ, estimate) => {
            dispatch(
              configActions.setConfigValue({
                type: 'overlayLoading',
                value: false,
              })
            )
          }
        )
      )
    }
  }

  const isBelow10K = estimate?.properties?.totalWithTax && estimate?.properties?.installmentThreshold && estimate?.properties?.totalWithTax < estimate?.properties?.installmentThreshold


  const buttonText = () => {
    if (paymentOption === 'pay_at_close') {
      if (agentAsHomeownerRepresentative === 'myself') {
        return 'Sign Service Agreement'
      }
      if (!stepPayAtClose) {
        return 'Next'
      }
      return 'Request Pay-at-Close'
    } else {
      if (propertyToBeSold) {
        return 'Sign Service Agreement'
      }
      return 'Terms of Service'
    }
  }

  return (
    <Grid className={classes.container} spacing={2}>
      {!stepPayAtClose && <HomeownershipConfirmation />}
      {agentIsHomeowner && (
        <Grid item xs={12} lg={4}>
          <Typography
            variant="body2"
            className={classes.question}
            style={{ marginTop: '24px' }}
          >
            What is your role in this transaction?
          </Typography>
          <Autocomplete
            label=""
            error={showWarnings && isEmpty(role)}
            value={
              contactInEstimate?.role === CONTACT_ROLE.AGENT_HOMEOWNER
                ? getContactRoleOpt().find(
                  (o) => o.key === CONTACT_ROLE.HOMEOWNER
                )
                : getContactRoleOpt().find((o) => o.key === role)
            }
            options={getContactRoleOpt()}
            onChange={(value: any) => {
              saveNewValue('role', value.key)
            }}
          />
        </Grid>
      )}


      {!stepPayAtClose &&
        (agentContact ||
          estimate?.properties?.propertyToBeSold ||
          agentIsHomeowner) && (
          <Grid
            style={{
              background: '#EAF2FE',
              borderRadius: '8px',
              padding: '1rem',
              border: '1px solid #ACCAFC',
            }}
          >
            <PropertyStatus isHomeowner={isHomeowner} />
          </Grid>
        )}

      {propertyToBeSold && showPayAtClose && !stepPayAtClose && estimate?.properties.financingStatus !== "PAY_AT_COMPLETION_RESTARTED" && (
        <PayAtClose />
      )}

      {showPayAtCompletionFlow && (
        <Grid item>
          <Scheduling isHomeowner={isHomeowner} />
        </Grid>
      )}

      {propertyToBeSold &&
        !agentContact &&
        paymentOption === 'pay_at_close' && (isBelow10K || estimate?.properties.financingRejectedOn || isBosscatFinancing) &&
        (!agentIsHomeowner ||
          agentAsHomeownerRepresentative === 'listing_agent') && (
          <ListingAgentInformation />
        )}
      {paymentOption === 'pay_at_close' &&
        stepPayAtClose && !isBelow10K && !isBosscatFinancing && <Financing handleRequestFinancing={submitStep} />}

      {agentAsHomeownerRepresentative === 'myself' &&
        paymentOption === 'pay_at_close' && (
          <>
            <ListingAgreement
              setListingAgreementDocument={setListingAgreementDocument}
              listingAgreementDocument={listingAgreementDocument}
            />
            {!propertyUnderContract && (
              <>
                <ComparableMarketAnalysis
                  comparableMarketAnalysis={comparableMarketAnalysis}
                  setComparableMarketAnalysis={setComparableMarketAnalysis}
                />
                <ApproximateHomeEquity />
              </>
            )}
          </>
        )}

      {/* {(paymentOption === 'pay_at_completion' || showPayAtCompletionFlow) && ( */}
      {showPayAtCompletionFlow && (
        <>
          {(paymentOption !== 'pay_at_close' || agentIsHomeowner) && (
            <SchedulerContact
              // value={scheduler || 'other'}
              showWarnings={showWarnings}
            />
          )}
          <AccessDetails />
        </>
      )}

      <BottomButtons
        buttonBack={{
          title: 'Back to Estimate',
          action: () => {
            goBack()
          },
        }}
        showNextButton={paymentOption === 'pay_at_close' ? isBelow10K || isBosscatFinancing ? true : !stepPayAtClose : true}
        buttonNext={{
          title: buttonText(),
          action: submitStep,
          disable: !validate(),
        }}
      />
    </Grid>
  )
}

export default Step1Homeowner
